import React, { useMemo } from 'react';
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';

import { useTimezone } from '../../../hooks/useTimezone';
import { ProfileEvent, ProfileEventKind } from './profileEvents';

/**
 * Events on a quiet rail, grouped by day (school timezone). Each event is
 * one line with a muted detail and its time; the dot's colour says what
 * kind of event it is.
 */

export const EVENT_KIND_LABEL: Record<ProfileEventKind, string> = {
  admission: 'Admission',
  enrollment: 'Enrollment',
  fees: 'Fees',
  letter: 'Letters',
};

const ProfileTimeline: React.FC<{ events: ProfileEvent[] }> = ({ events }) => {
  const theme = useTheme();
  const { formatDate, formatTime } = useTimezone();

  const dot: Record<ProfileEventKind, string> = {
    admission: theme.palette.primary.main,
    enrollment: theme.palette.success.main,
    fees: theme.palette.warning.main,
    letter: theme.palette.secondary.main,
  };

  const days = useMemo(() => {
    const groups: Array<{ day: string; items: ProfileEvent[] }> = [];
    events.forEach(e => {
      const day = formatDate(e.at, 'EEEE, dd MMM yyyy');
      const last = groups[groups.length - 1];
      if (last?.day === day) last.items.push(e);
      else groups.push({ day, items: [e] });
    });
    return groups;
  }, [events, formatDate]);

  return (
    <Stack spacing={2.5}>
      {days.map(({ day, items }) => (
        <Box key={day}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {day}
          </Typography>
          <Box sx={{ position: 'relative', pl: 2.5 }}>
            <Box sx={{ position: 'absolute', left: 4, top: 8, bottom: 8, width: 1.5, bgcolor: alpha(theme.palette.text.primary, 0.08) }} />
            <Stack spacing={1.5}>
              {items.map((e, i) => (
                <Stack key={`${e.at}-${i}`} direction="row" spacing={1.5} alignItems="flex-start" sx={{ position: 'relative' }}>
                  <Box sx={{
                    position: 'absolute', left: -20, top: 6, width: 9, height: 9, borderRadius: '50%',
                    bgcolor: dot[e.kind], boxShadow: `0 0 0 3px ${theme.palette.background.paper}`,
                  }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>{e.title}</Typography>
                    {e.detail && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                        {e.detail}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap', pt: 0.25 }}>
                    {formatTime(e.at)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

export default ProfileTimeline;
